import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import ms from 'ms'
import { AutomationTarget } from '../../../../types'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const ANIMATION_DURATION = ms('300ms')
const DRAG_CLOSE_THRESHOLD = 0.3 // 30% of panel height

export type SlideUpPanelSize =
  | 'mini'
  | 'tiny'
  | 'small'
  | 'large'
  | 'fullscreen'
  | 'default'

const SIZE_WIDTH_MAP: Record<SlideUpPanelSize, string> = {
  mini: '340px',
  tiny: '510px',
  small: '680px',
  large: '1020px',
  fullscreen: '100%',
  default: '680px',
}

export interface SlideUpPanelProps extends AutomationTarget {
  children?: ReactNode
  className?: string
  dimmer?: boolean
  onClose?: () => void
  open?: boolean
  size?: SlideUpPanelSize
  title?: ReactNode | string
}

const SlideUpPanel: React.FC<SlideUpPanelProps> = ({
  children,
  className,
  dimmer = true,
  onClose,
  open = false,
  size = 'default',
  title,
  elmId = 'slide-up-panel',
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimatingIn, setIsAnimatingIn] = useState(false)
  const [isAnimatingOut, setIsAnimatingOut] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const panelRef = useRef<HTMLDivElement>(null)
  const dragStartY = useRef(0)
  const panelHeight = useRef(0)
  const dragOffsetRef = useRef(0)
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closingInternallyRef = useRef(false)

  // Handle opening
  useEffect(() => {
    // Reset the internal close flag when parent acknowledges the close
    if (!open) {
      closingInternallyRef.current = false
    }

    // Don't open if we just closed internally (waiting for parent to sync)
    if (open && !isVisible && !closingInternallyRef.current) {
      setIsVisible(true)
      setIsAnimatingIn(true)
      setIsAnimatingOut(false)
      setDragOffset(0)
      document.body.style.overflow = 'hidden'

      // Clear any existing timer
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current)
      }

      animationTimerRef.current = setTimeout(() => {
        setIsAnimatingIn(false)
      }, ANIMATION_DURATION)
    }
    return undefined
  }, [open, isVisible])

  // Handle closing via prop
  useEffect(() => {
    if (!open && isVisible && !isAnimatingOut) {
      setIsAnimatingOut(true)
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current)
      }
      animationTimerRef.current = setTimeout(() => {
        setIsVisible(false)
        setIsAnimatingOut(false)
        document.body.style.overflow = ''
      }, ANIMATION_DURATION)
    }
    return undefined
  }, [open, isVisible, isAnimatingOut])

  // Cleanup
  useEffect(() => {
    return () => {
      document.body.style.overflow = ''
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current)
      }
    }
  }, [])

  const closePanel = useCallback(() => {
    if (isAnimatingOut) return
    closingInternallyRef.current = true
    setIsAnimatingOut(true)
    setDragOffset(0)
    setTimeout(() => {
      setIsVisible(false)
      setIsAnimatingOut(false)
      document.body.style.overflow = ''
      onClose?.()
    }, ANIMATION_DURATION)
  }, [isAnimatingOut, onClose])

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        closePanel()
      }
    },
    [closePanel]
  )

  // Keep ref in sync with state
  useEffect(() => {
    dragOffsetRef.current = dragOffset
  }, [dragOffset])

  // Mouse drag handlers
  useEffect(() => {
    if (!isDragging) return undefined

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientY - dragStartY.current
      if (delta > 0) {
        setDragOffset(delta)
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      const threshold = panelHeight.current * DRAG_CLOSE_THRESHOLD
      if (dragOffsetRef.current > threshold) {
        closePanel()
      } else {
        setDragOffset(0)
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, closePanel])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isAnimatingIn || isAnimatingOut) return
      e.preventDefault()
      if (panelRef.current) {
        panelHeight.current = panelRef.current.offsetHeight
      }
      dragStartY.current = e.clientY
      setIsDragging(true)
    },
    [isAnimatingIn, isAnimatingOut]
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (isAnimatingIn || isAnimatingOut) return
      if (panelRef.current) {
        panelHeight.current = panelRef.current.offsetHeight
      }
      dragStartY.current = e.touches[0].clientY
      setIsDragging(true)
    },
    [isAnimatingIn, isAnimatingOut]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return
      const delta = e.touches[0].clientY - dragStartY.current
      if (delta > 0) {
        setDragOffset(delta)
      }
    },
    [isDragging]
  )

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return
    setIsDragging(false)
    const threshold = panelHeight.current * DRAG_CLOSE_THRESHOLD
    // Use ref to get current value
    if (dragOffsetRef.current > threshold) {
      closePanel()
    } else {
      setDragOffset(0)
    }
  }, [isDragging, closePanel])

  if (!isVisible) return null

  // Calculate transform
  let transform = 'translateY(0)'
  if (isAnimatingOut) {
    transform = 'translateY(100%)'
  } else if (!isAnimatingIn && dragOffset > 0) {
    transform = `translateY(${dragOffset}px)`
  }

  const panelStyle: React.CSSProperties = {
    transform: isAnimatingIn ? undefined : transform,
    transition: isDragging
      ? 'none'
      : `transform ${ANIMATION_DURATION}ms ease-out`,
    width: size ? SIZE_WIDTH_MAP[size] : SIZE_WIDTH_MAP.default,
  }

  return createPortal(
    <div
      className={cx('slide-up-overlay', {
        'slide-up-overlay--visible': !isAnimatingOut,
        'slide-up-overlay--hidden': isAnimatingOut,
        'slide-up-overlay--no-dimmer': !dimmer,
      })}
      onClick={handleBackdropClick}
      role='presentation'
    >
      <div
        data-elm-id={elmId}
        ref={panelRef}
        className={cx('slide-up-panel', className, {
          'slide-up-panel--entering': isAnimatingIn,
        })}
        style={panelStyle}
        role='dialog'
        aria-modal='true'
      >
        <div
          className={cx('slide-up-handle')}
          data-elm-id={`${elmId}_handle`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          role='button'
          tabIndex={0}
          aria-label='Drag to close'
        >
          <div className={cx('slide-up-handle-bar')} />
        </div>
        {title && <div className={cx('slide-up-header')}>{title}</div>}
        <div className={cx('slide-up-content')}>{children}</div>
      </div>
    </div>,
    document.body
  )
}

export default SlideUpPanel
